import React, { FC, useEffect, useId, useRef, useState } from 'react'
import cx from 'classnames'
import { AriaButtonProps, useButton } from 'react-aria'
import { useRouter } from 'next/router'
import { useOnClickOutside } from 'usehooks-ts'

import { LinkProps } from '../Link'
import { Icons } from '../Icon'

import * as SC from './styled'
import { LangSwitchPosition } from './types'

export type LangSwitchProps = {
  className?: string
  button: AriaButtonProps & {
    label: string
  }
  links: LinkProps[]
  position?: LangSwitchPosition
}

const LangSwitch: FC<LangSwitchProps> = ({
  className,
  button,
  links,
  position = LangSwitchPosition.top,
}) => {
  const id = useId()
  const router = useRouter()
  const ref = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)

  const handleToggle = () => {
    setOpen(!open)
  }

  const refButton = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(
    {
      'aria-controls': id,
      onPress: handleToggle,
      'aria-expanded': open,
      'aria-label': button['aria-label'],
    },
    refButton
  )

  // close the switch on "escape"
  useEffect(() => {
    if (open === false) return
    const handlePressEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handlePressEscape)
    return () => {
      window.removeEventListener('keydown', handlePressEscape)
    }
  }, [open])

  // close when navigating
  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false)
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  // on focus outside
  useOnClickOutside(ref, () => {
    setOpen(false)
  })

  return (
    <SC.Container className={cx('LangSwitch', className)} ref={ref}>
      <SC.Trigger {...buttonProps} ref={refButton} $position={position}>
        <SC.Label>{button.label}</SC.Label>
        <SC.Chevron icon={Icons.chevronDown} aria-hidden $position={position} />
      </SC.Trigger>
      <SC.Wrapper $position={position}>
        <SC.List $position={position} hidden={!open} id={id}>
          {links.map((link, i) => (
            <SC.Item key={i}>
              <SC._Link {...link}>
                <span>{link.children ?? link.label ?? link.text}</span>
                {link['aria-current'] && (
                  <SC.Check icon={Icons.check} aria-hidden={true} />
                )}
              </SC._Link>
            </SC.Item>
          ))}
        </SC.List>
      </SC.Wrapper>
    </SC.Container>
  )
}

export default LangSwitch
